import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'umi';
import {
  addSeachRecord,
  delSeachRecord,
  seachRecordList,
} from '@intelligence/services/common';
import { SEARCH_HISTORY_TYPE, SEARCH_HISTORY_MAX } from '../_constants';
import { extractKeywords } from './helpers';
import type { ISearchRecordListResponse } from './types';

export const useSearchHistory = () => {
  const [history, setHistory] = useState<string[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(true);
  const mountedRef = useRef<boolean>(true);
  const userId: string = useSelector(
    (state: any) => state.home?.userInfo?.userid || '',
  );

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }

    let cancelled = false;
    setHistoryLoading(true);

    void (async (): Promise<void> => {
      try {
        const res = (await seachRecordList({
          type: SEARCH_HISTORY_TYPE,
          user_id: userId,
        })) as ISearchRecordListResponse;
        if (cancelled || !mountedRef.current) return;
        setHistory(extractKeywords(res?.data));
      } catch (err: unknown) {
        console.warn('[ReportSearch] fetch history failed:', err);
        if (!cancelled && mountedRef.current) {
          setHistory([]);
        }
      } finally {
        if (!cancelled && mountedRef.current) {
          setHistoryLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const addHistory = useCallback(
    (keyword: string): void => {
      const trimmed = keyword?.trim();
      if (!trimmed || !userId) return;
      setHistory(prev =>
        [trimmed, ...prev.filter(item => item !== trimmed)].slice(
          0,
          SEARCH_HISTORY_MAX,
        ),
      );
      void addSeachRecord({
        add_message: [
          {
            user_id: userId,
            type: SEARCH_HISTORY_TYPE,
            metric_id: trimmed,
            metric_name: trimmed,
            metric_cover_url: '',
            company_name: '',
            city_name: '',
            country_abbre: '',
          },
        ],
      })
        .then((res: ISearchRecordListResponse) => {
          if (!mountedRef.current) return;
          const keywords = extractKeywords(res?.data);
          if (keywords.length > 0) {
            setHistory(keywords);
          }
        })
        .catch((err: unknown) => {
          console.warn('[ReportSearch] add history failed:', err);
        });
    },
    [userId],
  );

  const removeHistory = useCallback(
    (keyword: string): void => {
      if (!userId) return;
      setHistory(prev => prev.filter(item => item !== keyword));
      delSeachRecord({
        metric_id: keyword,
        user_id: userId,
      }).catch((err: unknown) => {
        console.warn('[ReportSearch] delete history failed:', err);
      });
    },
    [userId],
  );

  const clearHistory = useCallback((): void => {
    setHistory([]);
  }, []);

  return { history, historyLoading, addHistory, removeHistory, clearHistory };
};
